import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { describeError } from '@/shared/hooks/useAsync';
import { useSession } from '@/shared/session/useSession';
import { Banner, Button, Field, PasswordField } from '@/shared/ui';
import { Logo } from '@/shared/ui/Logo';

export function SignInPage() {
  const { status, signIn, rejection } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  /*
   * Corregir es lo que borra el error, no volver a enviarlo.
   *
   * Antes el aviso rojo sobrevivía a toda la corrección: quien se equivocaba de contraseña seguía
   * leyendo «credenciales incorrectas» mientras escribía la buena, como si el sistema ya supiera
   * que iba a fallar de nuevo.
   */
  const edit = (apply: (value: string) => void) => (value: string) => {
    setError(null);
    apply(value);
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      // El recorte no es cosmético: el autocompletado de los teclados móviles agrega un espacio
      // después del correo, y ese espacio es una causa entera de «credenciales incorrectas» sobre
      // credenciales que estaban bien.
      await signIn(email.trim(), password);
    } catch (cause) {
      // CU-16 A1 · el servidor no distingue email inexistente de contraseña incorrecta, y hace
      // bien: decirlo confirmaría qué direcciones tienen cuenta.
      setError(describeError(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="auth-screen" onSubmit={onSubmit}>
      <div className="stack">
        <span className="brand">
          <Logo size={30} />
          <h1>FitLog</h1>
        </span>
        <p className="muted">Registrá lo que comés. El resto lo calcula el sistema.</p>
      </div>

      {/*
        El rechazo del servidor se muestra ANTES de que la persona intente nada. Si la sesión
        quedó afuera por un motivo que ella no puede adivinar —la lista blanca, por ejemplo—,
        enterarse recién después de escribir el correo y la contraseña es hacerle perder el
        tiempo dos veces.
      */}
      {error === null && rejection !== null && <Banner tone="danger">{rejection}</Banner>}
      {error !== null && <Banner tone="danger">{error}</Banner>}

      <div className="stack">
        <Field
          label="Correo electrónico"
          type="email"
          inputMode="email"
          autoComplete="email"
          // Sin esto el teclado del teléfono arranca en mayúscula y ofrece corrector sobre una
          // dirección de correo, que es exactamente donde ninguna de las dos cosas ayuda.
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          required
          value={email}
          onChange={(event) => edit(setEmail)(event.target.value)}
        />
        <PasswordField
          label="Contraseña"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => edit(setPassword)(event.target.value)}
        />
      </div>

      <Button type="submit" variant="primary" block disabled={submitting}>
        {submitting ? 'Entrando…' : 'Entrar'}
      </Button>

      <p className="small muted" style={{ textAlign: 'center' }}>
        ¿Todavía no tenés cuenta? <Link to="/registrarse">Crear una</Link>
      </p>
    </form>
  );
}
